import React, { useState } from 'react';
import { bool, number, arrayOf, shape, func } from 'prop-types';
import css from './PriceInsights.module.css';

// Simple SVG line chart component
const SimpleLineChart = ({ data, width = 300, height = 60, showAxes = false, className = '' }) => {
  if (!data || data.length === 0) return null;

  const padding = showAxes ? 30 : 5;
  const chartWidth = width - padding * 2;
  const chartHeight = height - padding * 2;

  // Find min and max values
  const values = data.map(d => d.value);
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const valueRange = maxValue - minValue || 1;

  // Calculate points for the line
  const points = data.map((d, i) => {
    const x = padding + (i / (data.length - 1)) * chartWidth;
    const y = padding + chartHeight - ((d.value - minValue) / valueRange) * chartHeight;
    return { x, y, label: d.label, value: d.value };
  });

  // Create path string for the line
  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`).join(' ');

  // Create path for area fill
  const areaPathD = `${pathD} L ${points[points.length - 1].x},${height -
    padding} L ${padding},${height - padding} Z`;

  return (
    <svg width={width} height={height} className={className}>
      {/* Area fill */}
      <path d={areaPathD} fill="rgba(139, 92, 246, 0.1)" stroke="none" />

      {/* Line */}
      <path
        d={pathD}
        fill="none"
        stroke="#8b5cf6"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Points */}
      {showAxes && points.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r="3" fill="#8b5cf6" />)}
    </svg>
  );
};

SimpleLineChart.propTypes = {
  data: arrayOf(
    shape({
      label: String,
      value: number.isRequired,
    })
  ).isRequired,
  width: number,
  height: number,
  showAxes: bool,
  className: String,
};

const PriceInsights = ({
  priceData,
  currentAveragePrice,
  lowestPrice,
  percentageDifference,
  onViewDetails,
}) => {
  const [showModal, setShowModal] = useState(false);

  const safePriceData = Array.isArray(priceData) ? priceData : [];
  const typicalPrice = safePriceData.length
    ? safePriceData.reduce((sum, p) => sum + Number(p?.value || 0), 0) / safePriceData.length
    : 0;

  const safeTypical = Number(typicalPrice) || 0;
  const safeCurrent = Number(currentAveragePrice) || 0;
  const diffPctRaw =
    safeTypical > 0
      ? ((safeCurrent - safeTypical) / safeTypical) * 100
      : Number(percentageDifference) || 0;
  const diffPctAbs = Math.abs(Math.round(diffPctRaw));

  const FAIR_THRESHOLD_PCT = 5;

  const status =
    diffPctRaw > FAIR_THRESHOLD_PCT ? 'high' : diffPctRaw < -FAIR_THRESHOLD_PCT ? 'low' : 'fair';

  const statusClass =
    status === 'high' ? css.statusHigh : status === 'low' ? css.statusLow : css.statusFair;

  // Mobile summary copy aligned with Good price / Ouch! / Solid ranges
  let summaryContent;
  if (status === 'low') {
    // Good price range
    summaryContent = (
      <>
        Prices are currently <span className={statusClass}>lower</span> than usual for this event
      </>
    );
  } else if (status === 'high') {
    // Ouch! range
    summaryContent = (
      <>
        Prices are currently <span className={statusClass}>higher</span> than usual for this event
      </>
    );
  } else {
    // Solid range
    summaryContent = (
      <>
        Prices are currently <span className={statusClass}>fair</span> for this event
      </>
    );
  }

  const handleViewInsights = () => {
    setShowModal(true);
    if (onViewDetails) {
      onViewDetails();
    }
  };

  return (
    <>
      {/* Compact Price Insights Bar */}
      <div className={css.priceInsightsBar}>
        <div className={css.insightsLeft}>
          <div className={css.iconWrapper}>
            <span className={css.insightsIcon}>📊</span>
          </div>
          <span className={css.insightsLabel}>Price Insights</span>

          <div className={css.miniChart}>
            <SimpleLineChart data={priceData} width={140} height={40} showAxes={false} />
          </div>

          <div className={css.priceInfo}>
            <span className={css.priceStatusLine}>{summaryContent}</span>
          </div>
        </div>

        <button className={css.viewInsightsButton} onClick={handleViewInsights}>
          View price insights
        </button>
      </div>

      {/* Detailed Modal */}
      {showModal && (
        <div className={css.modalOverlay} onClick={() => setShowModal(false)}>
          <div className={css.modalContent} onClick={e => e.stopPropagation()}>
            <div className={css.modalHeader}>
              <h3 className={css.modalTitle}>Ticket Price Insights</h3>
              <button className={css.closeButton} onClick={() => setShowModal(false)}>
                ✕
              </button>
            </div>

            <div className={css.modalBody}>
              {/* Large Chart */}
              <div className={css.largeChartContainer}>
                <SimpleLineChart
                  data={priceData}
                  width={600}
                  height={200}
                  showAxes={true}
                  className={css.largeChart}
                />
              </div>

              {/* Price Summary */}
              <div className={css.priceSummary}>
                <div className={css.currentPrice}>
                  <span className={css.priceLabel}>● Current average price:</span>
                  <span className={css.priceValue}>${currentAveragePrice}</span>
                </div>
              </div>

              {/* Insights Text */}
              <div className={css.insightsText}>
                {isLowerThanUsual ? (
                  <p>
                    Prices are lower than usual for this event. We recommend buying now as prices
                    are likely to increase closer to the event date.
                  </p>
                ) : (
                  <p>
                    Prices are higher than usual for this event. Consider waiting a bit as prices
                    may decrease as the event date approaches.
                  </p>
                )}
              </div>

              {/* Best Value */}
              {lowestPrice && (
                <div className={css.bestValue}>
                  <strong>Best value:</strong> Section 211 tickets at ${lowestPrice}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

PriceInsights.propTypes = {
  priceData: arrayOf(
    shape({
      label: String,
      value: number.isRequired,
    })
  ).isRequired,
  currentAveragePrice: number.isRequired,
  lowestPrice: number,
  percentageDifference: number.isRequired,
  onViewDetails: func,
};

PriceInsights.defaultProps = {
  lowestPrice: null,
  onViewDetails: null,
};

export default PriceInsights;
